/**
 * The Changes bite's content (spec 028): a turn's changes in plain words.
 * The story form is a card per scene in story order (badge, name, Open),
 * the first three shown; the scene form is the lines alone. Each line's
 * tooltip holds the technical summaries of the steps it came from (D-4).
 */
import React, { useState } from 'react';
import { FaDesktop, FaExclamationTriangle, FaMap, FaMobileAlt } from 'react-icons/fa';
import type { Orientation } from '../../../../Shared/src/@types';
import {
  avatarColour, cardName, initialsOf, lineText, ORIENTATION_TAG, reportTitle,
  type ChangeCard, type ChangeLine, type ChangesGroup,
} from './groupChanges';
import styles from './ChangesReport.module.scss';

/** Cards shown before "Show N more scenes" (D-5). */
export const VISIBLE_CARDS = 3;
const MAX_AVATARS = 3;

export const OrientationTag: React.FC<{ orientation: Orientation }> = ({ orientation }) => (
  <span className={styles.tag} data-testid="changes-orientation">
    {orientation === 'desktop'
      ? <FaDesktop aria-hidden />
      : <FaMobileAlt aria-hidden className={orientation === 'landscape' ? styles.rotated : undefined} />}
    {ORIENTATION_TAG[orientation]}
  </span>
);

const Avatars: React.FC<{ who: string[] }> = ({ who }) => {
  if (!who.length) return null;
  const shown = who.slice(0, MAX_AVATARS);
  return (
    <span className={styles.avatars} aria-hidden>
      {shown.map((name) => (
        <span key={name} className={styles.avatar} style={{ background: avatarColour(name) }} data-testid="changes-avatar" data-name={name}>
          {initialsOf(name)}
        </span>
      ))}
      {who.length > MAX_AVATARS && <span className={`${styles.avatar} ${styles.more}`} data-testid="changes-avatar-more">+{who.length - MAX_AVATARS}</span>}
    </span>
  );
};

const Line: React.FC<{ line: ChangeLine }> = ({ line }) => (
  <li
    className={`${styles.line} ${line.warn ? styles.warn : ''}`}
    title={line.summaries.join('\n')}
    data-testid="changes-line"
  >
    <Avatars who={line.who} />
    {line.warn && <FaExclamationTriangle className={styles.warnIcon} aria-label="Warning" />}
    <span className={styles.text} data-testid="changes-line-text">{lineText(line)}</span>
    {line.orientation && <OrientationTag orientation={line.orientation} />}
  </li>
);

const Card: React.FC<{ card: ChangeCard; onOpen?: () => void }> = ({ card, onOpen }) => (
  <div className={styles.card} data-testid="changes-card" data-scene={card.scene ?? 'story-map'}>
    <div className={styles.cardHeader}>
      {card.storyMap
        ? <span className={styles.badge} data-testid="changes-card-map"><FaMap aria-label="Story map" /></span>
        : card.stop !== undefined && <span className={styles.badge} data-testid="changes-card-badge">{card.stop}</span>}
      <span className={styles.cardName} data-testid="changes-card-name">{card.storyMap ? card.sceneName : cardName(card.sceneName, card.stop)}</span>
      {onOpen && (
        <button type="button" className={styles.open} onClick={onOpen} data-testid="changes-card-open">Open</button>
      )}
    </div>
    <ul className={styles.lines}>
      {card.lines.map((line, i) => <Line key={i} line={line} />)}
    </ul>
    {card.shared && <p className={styles.sharedNote} data-testid="changes-card-shared">Also changes in other stories that use this scene</p>}
  </div>
);

export interface ChangesReportProps {
  group: ChangesGroup;
  /** `story`: cards with Open and collapsing; `scene`: the lines alone (the scene assistant). */
  form: 'story' | 'scene';
  /** Story form: the Open action of a card's scene, or undefined to hide Open for it. */
  openFor?: (sceneKey: string) => (() => void) | undefined;
}

export const ChangesReport: React.FC<ChangesReportProps> = ({ group, form, openFor }) => {
  const [expanded, setExpanded] = useState(false);
  const header = (title: string) => (
    <div className={styles.header}>
      <span className={styles.title} data-testid="changes-title">{title}</span>
      {group.orientation && <OrientationTag orientation={group.orientation} />}
    </div>
  );

  if (form === 'scene') {
    return (
      <div className={styles.report} data-testid="changes-report">
        {header('CHANGES')}
        <ul className={styles.lines}>
          {group.cards.flatMap((c) => c.lines).map((line, i) => <Line key={i} line={line} />)}
        </ul>
      </div>
    );
  }

  const hidden = Math.max(0, group.cards.length - VISIBLE_CARDS);
  const cards = expanded ? group.cards : group.cards.slice(0, VISIBLE_CARDS);
  return (
    <div className={styles.report} data-testid="changes-report">
      {header(reportTitle(group))}
      {cards.map((card) => (
        <Card key={card.scene ?? 'story-map'} card={card} onOpen={card.scene && !card.storyMap ? openFor?.(card.scene) : undefined} />
      ))}
      {hidden > 0 && (
        <button type="button" className={styles.more} onClick={() => setExpanded((e) => !e)} data-testid="changes-more">
          {expanded ? 'Show fewer' : `Show ${hidden} more scene${hidden === 1 ? '' : 's'}`}
        </button>
      )}
    </div>
  );
};

export default ChangesReport;
